import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { LogOut, Plus, Edit, Trash2, Search, X, Filter } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import ImportExport from '../components/admin/ImportExport';
import FixedFooter from '@/components/common/FixedFooter';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  
  // Suppress ResizeObserver errors
  useEffect(() => {
    const handleError = (e) => {
      if (e.message && e.message.includes('ResizeObserver loop completed')) {
        e.preventDefault();
        e.stopImmediatePropagation();
        return false;
      }
    };
    
    const handleUnhandledRejection = (e) => {
      if (e.reason && e.reason.message && e.reason.message.includes('ResizeObserver')) {
        e.preventDefault();
        return false;
      }
    };
    
    window.addEventListener('error', handleError, true);
    window.addEventListener('unhandledrejection', handleUnhandledRejection, true);
    
    // Also suppress console errors
    const originalError = console.error;
    console.error = (...args) => {
      if (args[0] && typeof args[0] === 'string' && args[0].includes('ResizeObserver')) {
        return;
      }
      originalError.apply(console, args);
    };
    
    return () => {
      window.removeEventListener('error', handleError, true);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection, true);
      console.error = originalError;
    };
  }, []);
  const [menuItems, setMenuItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [showCustomCategory, setShowCustomCategory] = useState(false);
  const [activeTab, setActiveTab] = useState('menu');
  const [events, setEvents] = useState([]);
  const [newEvent, setNewEvent] = useState({ title: '', image: null });
  const [filters, setFilters] = useState({
    outlet: 'all',
    category: 'all',
    subcategory: 'all',
    available: 'all'
  });
  const [showFilters, setShowFilters] = useState(false);
  const [formData, setFormData] = useState({
    outlet: 'cafe-plus',
    menu_type: 'food-menu',
    category: '',
    subcategory: '',
    name: '',
    description: '',
    prices_in_inr: [''],
    veg: 'veg',
    image_url: '',
    tags: [],
    available: true,
    customCategory: '',
  });

  useEffect(() => {
    checkAuth();
    fetchMenuItems();
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/events`);
      setEvents(response.data);
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  };

  const handleAddEvent = async (e) => {
    e.preventDefault();
    if (!newEvent.title || !newEvent.image) {
      toast.error('Please fill all fields');
      return;
    }

    const formData = new FormData();
    formData.append('title', newEvent.title);
    formData.append('image', newEvent.image);

    try {
      const token = localStorage.getItem('admin-token');
      await axios.post(`${API}/events`, formData, {
        headers: { 
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`
        }
      });
      toast.success('Event added successfully!');
      setNewEvent({ title: '', image: null });
      fetchEvents();
    } catch (error) {
      toast.error('Failed to add event');
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;

    try {
      const token = localStorage.getItem('admin-token');
      await axios.delete(`${API}/events/${eventId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Event deleted successfully');
      fetchEvents();
    } catch (error) {
      toast.error('Failed to delete event');
    }
  };

  useEffect(() => {
    filterItems();
  }, [menuItems, searchQuery, filters]);

  const checkAuth = () => {
    const token = localStorage.getItem('admin-token');
    if (!token) {
      toast.error('Please login first');
      navigate('/admin/login');
    }
  };

  const fetchMenuItems = async () => {
    try {
      const token = localStorage.getItem('admin-token');
      
      if (!token) {
        console.error('No token found');
        navigate('/admin/login');
        return;
      }
      
      // Use admin endpoint to get ALL items (including unavailable)
      try {
        const response = await axios.get(`${API}/menu/admin/items`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        console.log('Admin fetched items count:', response.data.length);
        
        // Debug availability status
        const availableCount = response.data.filter(item => item.available !== false).length;
        const unavailableCount = response.data.length - availableCount;
        console.log(`Items: ${availableCount} available, ${unavailableCount} unavailable`);
        
        setMenuItems(response.data);
      } catch (adminError) {
        console.warn('Admin endpoint failed, using regular endpoint:', adminError.message);
        // Fallback to regular endpoint
        const response = await axios.get(`${API}/menu/items`);
        console.log('Regular fetched items count:', response.data.length);
        setMenuItems(response.data);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching menu items:', error.response?.data || error.message);
      if (error.response?.status === 401) {
        localStorage.removeItem('admin-token');
        navigate('/admin/login');
      }
      setLoading(false);
    }
  };

  const filterItems = () => {
    let filtered = [...menuItems];

    // Apply filters
    if (filters.outlet !== 'all') {
      filtered = filtered.filter(item => item.outlet === filters.outlet);
    }
    if (filters.category !== 'all') {
      filtered = filtered.filter(item => item.category === filters.category);
    }
    if (filters.subcategory !== 'all') {
      filtered = filtered.filter(item => item.subcategory === filters.subcategory);
    }
    if (filters.available !== 'all') {
      const isAvailable = filters.available === 'true';
      filtered = filtered.filter(item => item.available === isAvailable);
    }

    // Apply search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(item =>
        item.name.toLowerCase().includes(query) ||
        item.category.toLowerCase().includes(query) ||
        item.outlet.toLowerCase().includes(query)
      );
    }

    setFilteredItems(filtered);
  };

  const fetchCategoriesByOutlet = (outlet) => {
    const outletItems = menuItems.filter(item => item.outlet === outlet);
    const categories = [...new Set(outletItems.map(item => item.category))].filter(Boolean);
    setAvailableCategories(categories);
  };

  const handleLogout = () => {
    localStorage.removeItem('admin-token');
    toast.success('Logged out successfully');
    navigate('/admin/login');
  };

  const handleAddNew = () => {
    setEditingItem(null);
    setFormData({
      outlet: 'cafe-plus',
      menu_type: 'food-menu',
      category: '',
      subcategory: '',
      name: '',
      description: '',
      prices_in_inr: [''],
      veg: 'veg',
      image_url: '',
      tags: [],
      available: true,
      customCategory: '',
    });
    setShowCustomCategory(false);
    fetchCategoriesByOutlet('cafe-plus');
    setShowDialog(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      outlet: item.outlet,
      menu_type: item.menu_type,
      category: item.category,
      subcategory: item.subcategory || '',
      name: item.name,
      description: item.description,
      prices_in_inr: item.prices_in_inr.map(String),
      veg: item.veg || 'none',
      image_url: item.image_url || '',
      tags: item.tags || [],
      available: item.available !== false, // Default to true if undefined
      customCategory: '',
    });
    setShowCustomCategory(false);
    fetchCategoriesByOutlet(item.outlet);
    setShowDialog(true);
  };



  const handleDelete = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;

    try {
      const token = localStorage.getItem('admin-token');
      await axios.delete(`${API}/menu/items/${itemId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Item deleted successfully');
      fetchMenuItems();
    } catch (error) {
      console.error('Error deleting item:', error);
      toast.error('Failed to delete item');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Only send fields that are being updated
    const submitData = {
      name: formData.name,
      description: formData.description,
      prices_in_inr: formData.prices_in_inr.map(p => parseInt(p)).filter(p => !isNaN(p)),
      veg: formData.veg === 'none' ? '' : formData.veg,
      category: formData.category,
      subcategory: formData.subcategory,
      outlet: formData.outlet,
      menu_type: formData.menu_type,
      image_url: formData.image_url,
      tags: formData.tags,
      available: formData.available
    };



    try {
      const token = localStorage.getItem('admin-token');
      if (editingItem) {
        console.log('Updating item:', editingItem.id);
        const response = await axios.put(`${API}/menu/items/${editingItem.id}`, submitData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Item updated successfully');
      } else {
        await axios.post(`${API}/menu/items`, submitData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Item added successfully');
      }
      setShowDialog(false);
      await fetchMenuItems();
    } catch (error) {
      console.error('Error saving item:', error.response?.data || error.message);
      toast.error('Failed to save item');
    }
  };

  const updatePrice = (index, value) => {
    const newPrices = [...formData.prices_in_inr];
    newPrices[index] = value;
    setFormData({ ...formData, prices_in_inr: newPrices });
  };

  const addPrice = () => {
    setFormData({ ...formData, prices_in_inr: [...formData.prices_in_inr, ''] });
  };

  const removePrice = (index) => {
    const newPrices = formData.prices_in_inr.filter((_, i) => i !== index);
    setFormData({ ...formData, prices_in_inr: newPrices });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" style={{ borderColor: 'var(--burgundy)' }}></div>
          <p style={{ color: 'var(--charcoal)' }}>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-10">
      {/* Header */}
      <header className="glass-burgundy border-b" style={{ borderColor: 'rgba(224, 178, 59, 0.2)' }}>
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <h1 className="text-3xl" style={{ color: 'var(--burgundy)' }} data-testid="dashboard-title">Admin Dashboard</h1>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 btn-secondary"
              data-testid="logout-button"
            >
              <LogOut size={18} />
              Logout
            </button>
          </div>

          {/* Tabs */}
          <div className="mt-6 flex gap-4">
            <button
              onClick={() => setActiveTab('menu')}
              className={`px-4 py-2 rounded-lg font-medium ${
                activeTab === 'menu' ? 'btn-primary' : 'glass'
              }`}
            >
              Menu Items
            </button>
            <button
              onClick={() => setActiveTab('import-export')}
              className={`px-4 py-2 rounded-lg font-medium ${
                activeTab === 'import-export' ? 'btn-primary' : 'glass'
              }`}
            >
              Import / Export
            </button>
            <button
              onClick={() => setActiveTab('events')}
              className={`px-4 py-2 rounded-lg font-medium ${
                activeTab === 'events' ? 'btn-primary' : 'glass'
              }`}
            >
              Upcoming Events
            </button>
          </div>

          {activeTab === 'menu' && (
            <div className="mt-4 space-y-4">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2" size={20} style={{ color: 'var(--charcoal)' }} />
                  <Input
                    type="text"
                    placeholder="Search items..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 rounded-full glass"
                    data-testid="search-input"
                  />
                </div>
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`btn-secondary flex items-center gap-2 ${showFilters ? 'bg-burgundy-light' : ''}`}
                >
                  <Filter size={20} />
                  Filters
                </button>
                <button
                  onClick={handleAddNew}
                  className="btn-gold flex items-center gap-2"
                  data-testid="add-item-button"
                >
                  <Plus size={20} />
                  Add New Item
                </button>
              </div>
              
              {showFilters && (
                <div className="glass rounded-2xl p-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Outlet</label>
                      <Select value={filters.outlet} onValueChange={(value) => setFilters({...filters, outlet: value})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Outlets</SelectItem>
                          <SelectItem value="cafe-plus">Café Plus</SelectItem>
                          <SelectItem value="plus-one">Plus One</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-2">Category</label>
                      <Select value={filters.category} onValueChange={(value) => setFilters({...filters, category: value, subcategory: 'all'})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Categories</SelectItem>
                          {[...new Set(menuItems.filter(item => filters.outlet === 'all' || item.outlet === filters.outlet).map(item => item.category))].map(category => (
                            <SelectItem key={category} value={category}>
                              {category.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-2">Subcategory</label>
                      <Select value={filters.subcategory} onValueChange={(value) => setFilters({...filters, subcategory: value})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Subcategories</SelectItem>
                          {[...new Set(menuItems.filter(item => 
                            item.subcategory && 
                            (filters.outlet === 'all' || item.outlet === filters.outlet) &&
                            (filters.category === 'all' || item.category === filters.category)
                          ).map(item => item.subcategory))].map(subcategory => (
                            <SelectItem key={subcategory} value={subcategory}>
                              {subcategory.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-2">Availability</label>
                      <Select value={filters.available} onValueChange={(value) => setFilters({...filters, available: value})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Items</SelectItem>
                          <SelectItem value="true">Available Only</SelectItem>
                          <SelectItem value="false">Unavailable Only</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div className="mt-4 flex gap-2">
                    <button
                      onClick={() => setFilters({ outlet: 'all', category: 'all', subcategory: 'all', available: 'all' })}
                      className="btn-secondary text-sm"
                    >
                      Clear Filters
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </header>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        {activeTab === 'import-export' && (
          <ImportExport onImportComplete={fetchMenuItems} />
        )}

        {activeTab === 'events' && (
          <div className="mb-8">
            {/* Add Event Form */}
            <form onSubmit={handleAddEvent} className="glass rounded-2xl p-6 mb-6">
              <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--burgundy)' }}>
                Add New Event
              </h3>
              <div className="grid sm:grid-cols-2 gap-4">
                <input
                  type="text"
                  placeholder="Event Title"
                  value={newEvent.title}
                  onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })}
                  className="px-4 py-2 rounded-lg border glass"
                  required
                />
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setNewEvent({ ...newEvent, image: e.target.files[0] })}
                  className="px-4 py-2 rounded-lg border glass"
                  required
                />
              </div>
              <button type="submit" className="btn-primary mt-4 flex items-center gap-2">
                <Plus size={20} />
                Add Event
              </button>
            </form>

            {/* Events Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {events.map((event) => (
                <div key={event.id} className="glass rounded-2xl overflow-hidden">
                  <img
                    src={`${BACKEND_URL}${event.image_url}`}
                    alt={event.title}
                    className="w-full h-48 object-cover"
                  />
                  <div className="p-4">
                    <h3 className="text-lg font-semibold mb-2" style={{ color: 'var(--burgundy)' }}>
                      {event.title}
                    </h3>
                    <button
                      onClick={() => handleDeleteEvent(event.id)}
                      className="btn-secondary text-red-600 border-red-600 hover:bg-red-600 hover:text-white flex items-center gap-2"
                    >
                      <Trash2 size={16} />
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {events.length === 0 && (
              <div className="text-center py-20">
                <p className="text-xl" style={{ color: 'var(--charcoal)' }}>No events added yet</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'menu' && (
          <>
            <div className="mb-4 flex items-center justify-between">
              <div className="text-sm" style={{ color: 'var(--charcoal)' }}>
                Showing {filteredItems.length} of {menuItems.length} items
              </div>
              <div className="text-sm flex gap-4" style={{ color: 'var(--charcoal)' }}>
                <span className="text-green-600">
                  Available: {filteredItems.filter(item => item.available !== false).length}
                </span>
                <span className="text-red-600">
                  Unavailable: {filteredItems.filter(item => item.available === false).length}
                </span>
              </div>
            </div>

        <div className="grid gap-4">
          {filteredItems.map((item) => (
            <div
              key={item.id}
              className={`glass rounded-2xl p-6 hover:shadow-xl transition-all duration-300 ${
                item.available === false ? 'border-2 border-red-300 bg-red-50/50' : ''
              }`}
              data-testid={`menu-item-${item.id}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className={`text-xl font-semibold ${
                      item.available === false ? 'text-red-600 line-through' : ''
                    }`} style={{ color: item.available === false ? '#dc2626' : 'var(--burgundy)' }}>{item.name}</h3>
                    <span className="text-xs px-3 py-1 rounded-full" style={{ 
                      backgroundColor: item.outlet === 'cafe-plus' ? 'var(--burgundy-light)' : 'var(--charcoal)',
                      color: item.outlet === 'cafe-plus' ? 'var(--burgundy)' : 'var(--gold)'
                    }}>
                      {item.outlet === 'cafe-plus' ? 'Café Plus' : 'Plus One'}
                    </span>
                    {item.available === false && (
                      <span className="text-xs px-3 py-1 rounded-full bg-red-500 text-white font-medium">UNAVAILABLE</span>
                    )}
                    {item.veg === 'veg' && (
                      <span className="text-xs px-2 py-1 rounded bg-green-100 text-green-700">Veg</span>
                    )}
                    {item.veg === 'non-veg' && (
                      <span className="text-xs px-2 py-1 rounded bg-red-100 text-red-700">Non-Veg</span>
                    )}
                  </div>
                  <p className={`text-sm mb-2 ${
                    item.available === false ? 'text-red-500 opacity-70' : 'opacity-80'
                  }`} style={{ color: item.available === false ? '#ef4444' : 'var(--charcoal)' }}>
                    {item.description}
                  </p>
                  <div className={`flex items-center gap-4 text-sm ${
                    item.available === false ? 'text-red-500 opacity-70' : ''
                  }`}>
                    <span className="font-semibold" style={{ color: 'var(--gold)' }}>
                      ₹{item.prices_in_inr.join(' / ₹')}
                    </span>
                    <span style={{ color: 'var(--charcoal)', opacity: 0.6 }}>•</span>
                    <span style={{ color: 'var(--charcoal)', opacity: 0.6 }}>
                      {item.category.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleEdit(item)}
                    className="p-2 rounded-lg hover:bg-blue-50 text-blue-600"
                    data-testid={`edit-button-${item.id}`}
                  >
                    <Edit size={18} />
                  </button>
                  <button
                    onClick={() => handleDelete(item.id)}
                    className="p-2 rounded-lg hover:bg-red-50 text-red-600"
                    data-testid={`delete-button-${item.id}`}
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

            {filteredItems.length === 0 && (
              <div className="text-center py-20">
                <p className="text-xl" style={{ color: 'var(--charcoal)' }}>No items found</p>
              </div>
            )}
          </>
        )}
      </main>

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Edit Menu Item' : 'Add New Menu Item'}</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Outlet</label>
                <Select value={formData.outlet} onValueChange={(value) => {
                  setFormData({ 
                    ...formData, 
                    outlet: value,
                    menu_type: value === 'plus-one' ? 'bar-menu' : 'food-menu',
                    category: '',
                    customCategory: ''
                  });
                  setShowCustomCategory(false);
                  fetchCategoriesByOutlet(value);
                }}>
                  <SelectTrigger data-testid="outlet-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cafe-plus">Café Plus</SelectItem>
                    <SelectItem value="plus-one">Plus One</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Veg/Non-Veg</label>
                <Select value={formData.veg} onValueChange={(value) => setFormData({ ...formData, veg: value })}>
                  <SelectTrigger data-testid="veg-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="veg">Veg</SelectItem>
                    <SelectItem value="non-veg">Non-Veg</SelectItem>
                    <SelectItem value="both">Veg & Non-Veg</SelectItem>
                    <SelectItem value="none">None</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Item Name *</label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                placeholder="e.g. Butter Chicken"
                data-testid="name-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Description *</label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
                placeholder="Describe the dish..."
                rows={3}
                data-testid="description-input"
              />
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Category *</label>
                {!showCustomCategory ? (
                  <div className="space-y-2">
                    <Select 
                      value={formData.category} 
                      onValueChange={(value) => {
                        // Use setTimeout to avoid ResizeObserver issues
                        setTimeout(() => {
                          if (value === 'custom') {
                            setShowCustomCategory(true);
                            setFormData({ ...formData, category: '', subcategory: '' });
                          } else {
                            setFormData({ ...formData, category: value, subcategory: '' });
                          }
                        }, 0);
                      }}
                    >
                      <SelectTrigger data-testid="category-select">
                        <SelectValue placeholder="Select existing category or create new" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableCategories.length > 0 ? (
                          availableCategories.map((category) => (
                            <SelectItem key={category} value={category}>
                              {category.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                            </SelectItem>
                          ))
                        ) : (
                          <SelectItem value="no-categories" disabled>
                            No existing categories
                          </SelectItem>
                        )}
                        <SelectItem value="custom">+ Create New Category</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Input
                      value={formData.customCategory}
                      onChange={(e) => setFormData({ ...formData, customCategory: e.target.value, category: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                      placeholder="Enter new category name"
                      data-testid="custom-category-input"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setShowCustomCategory(false);
                        setFormData({ ...formData, category: '', customCategory: '' });
                      }}
                      className="text-sm text-blue-600 hover:text-blue-700"
                    >
                      ← Back to existing categories
                    </button>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Subcategory</label>
                {formData.category === 'wine' ? (
                  <Select 
                    value={formData.subcategory} 
                    onValueChange={(value) => setFormData({ ...formData, subcategory: value })}
                  >
                    <SelectTrigger data-testid="wine-subcategory-select">
                      <SelectValue placeholder="Select wine type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="red-wine-per-glass-bottle">RED WINE PER GLASS / BOTTLE</SelectItem>
                      <SelectItem value="white-wine-per-glass-bottle">WHITE WINE PER GLASS / BOTTLE</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (formData.category === 'liquor' || formData.category === 'liquors') ? (
                  <Select 
                    value={formData.subcategory} 
                    onValueChange={(value) => setFormData({ ...formData, subcategory: value })}
                  >
                    <SelectTrigger data-testid="liquor-subcategory-select">
                      <SelectValue placeholder="Select liquor type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="single-malt-whisky">Single Malt Whisky</SelectItem>
                      <SelectItem value="brandy">Brandy</SelectItem>
                      <SelectItem value="rum">Rum</SelectItem>
                      <SelectItem value="liquers-tequila">Liquers & Tequila</SelectItem>
                      <SelectItem value="gin">Gin</SelectItem>
                      <SelectItem value="vodka">Vodka</SelectItem>
                      <SelectItem value="domestic-whisky">Domestic Whisky</SelectItem>
                      <SelectItem value="american-irish-whisky">American & Irish Whisky</SelectItem>
                      <SelectItem value="premium-scotch-whisky">Premium Scotch Whisky</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    value={formData.subcategory}
                    onChange={(e) => setFormData({ ...formData, subcategory: e.target.value })}
                    placeholder="Optional"
                    data-testid="subcategory-input"
                  />
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Prices (INR) *</label>
              {formData.prices_in_inr.map((price, index) => (
                <div key={index} className="flex gap-2 mb-2">
                  <Input
                    type="number"
                    value={price}
                    onChange={(e) => updatePrice(index, e.target.value)}
                    required
                    placeholder="e.g. 350"
                    data-testid={`price-input-${index}`}
                    className="[&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [-moz-appearance:textfield]"
                  />
                  {formData.prices_in_inr.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removePrice(index)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded"
                    >
                      <X size={18} />
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={addPrice}
                className="text-sm text-blue-600 hover:text-blue-700"
              >
                + Add another price
              </button>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Image URL (Optional)</label>
              <Input
                value={formData.image_url}
                onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                placeholder="https://..."
                data-testid="image-url-input"
              />
            </div>

            <div className="flex items-center gap-2 p-3 border rounded-lg bg-gray-50">
              <input
                type="checkbox"
                id="available-checkbox"
                checked={formData.available === true}
                onChange={(e) => {
                  setFormData(prev => ({ ...prev, available: e.target.checked }));
                }}
                className="w-4 h-4 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500"
              />
              <label htmlFor="available-checkbox" className="text-sm font-medium cursor-pointer">
                Item is available for customers
              </label>
              <span className={`text-xs ml-2 font-medium px-2 py-1 rounded ${
                formData.available ? 'text-green-700 bg-green-100' : 'text-red-700 bg-red-100'
              }`}>
                {formData.available ? 'AVAILABLE' : 'UNAVAILABLE'}
              </span>
            </div>

            <div className="flex gap-3 pt-4">
              <button type="button" onClick={() => setShowDialog(false)} className="btn-secondary flex-1">
                Cancel
              </button>
              <button type="submit" className="btn-primary flex-1" data-testid="submit-button">
                {editingItem ? 'Update Item' : 'Add Item'}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Fixed Footer */}
      <FixedFooter />
    </div>
  );
};

export default AdminDashboard;